import React from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';

import { FacetRow } from '../components/FacetRow';
import { TileSkeleton } from '../components/ProductTile';
import { withInspiration, INSPIRATION } from '../components/InspirationBlock';
import Seo from '../components/Seo';
import { useLang } from '../i18n';
import { useCategories, useProducts } from '../lib/useCatalog';

const PER_PAGE = 24;

/**
 * Pagina mondo: i prodotti di un'intera macro-area, con le sue categorie come
 * primo filtro. Serve a far vedere oggetti subito, invece di far scegliere una
 * voce in un elenco prima di mostrare qualcosa.
 */
export default function WorldDetail() {
  const { slug } = useParams();
  const { lang, t } = useLang();
  const [params, setParams] = useSearchParams();

  const cats = useCategories(lang);
  const world = cats.data?.macros?.find((m) => m.slug === slug);

  const page = Math.max(1, Math.trunc(Number(params.get('page'))) || 1);
  const shade = params.get('shade') || '';

  const goToPage = (n) => {
    const next = new URLSearchParams(params);
    next.set('page', String(n));
    setParams(next);
  };

  const setFilter = (key, value) => {
    const next = new URLSearchParams(params);
    if (!value || next.get(key) === value) next.delete(key);
    else next.set(key, value);
    next.delete('page');
    setParams(next);
  };

  const products = useProducts({ macro_slug: slug, shade, lang, page, per_page: PER_PAGE });
  const items = products.data?.items ?? [];
  const pages = Math.max(1, Math.ceil((products.data?.total ?? 0) / PER_PAGE));

  if (cats.data && !world) {
    return (
      <div className="wrap py-24 text-center">
        <Seo title={t('notFound.seoTitle')} noIndex />
        <h1 className="display-xl">404</h1>
        <p className="prosa mt-4">{t('catalog.notFound')}</p>
        <Link className="btn btn-line mt-8 inline-flex" to="/catalogo">
          {t('catalog.backToCatalog')}
        </Link>
      </div>
    );
  }

  return (
    <>
      <Seo
        title={world?.copy?.seo_title || world?.label || t('catalog.title')}
        suffix={world?.copy?.seo_title ? '' : undefined}
        description={world?.copy?.seo_meta || t('catalog.seoDesc')}
        path={`/catalogo/mondo/${slug}`}
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'Home', item: '/' },
            { '@type': 'ListItem', position: 2, name: t('catalog.title'), item: '/catalogo' },
            { '@type': 'ListItem', position: 3, name: world?.label || '', item: `/catalogo/mondo/${slug}` },
          ],
        }}
      />

      <nav aria-label="breadcrumb" className="wrap pt-10">
        <Link className="label hover:text-marsala-dark" to="/catalogo">
          {t('catalog.title')}
        </Link>
      </nav>

      <header className="wrap pt-4 pb-8">
        <h1 className="display-xl">{world?.label || ' '}</h1>
        {world?.copy?.lead && (
          <p className="accento mt-5 text-xl italic text-inchiostro/70">{world.copy.lead}</p>
        )}
        <p className="prosa mt-4 max-w-xl">{world?.copy?.body || t('catalog.worldLead')}</p>
      </header>

      {world && (
        <div className="wrap pb-8">
          <div className="space-y-5 py-7">
            <div className="grid gap-3 md:grid-cols-[110px_1fr] md:items-baseline">
              <p className="text-[14px] font-bold text-inchiostro/70">
                {t('common.category')}
              </p>
              <div className="flex flex-wrap gap-2">
                {world.categories.map((c) => (
                  <Link
                    key={c.slug}
                    to={`/catalogo/${c.slug}`}
                    data-testid={`world-cat-${c.slug}`}
                    className="border border-inchiostro/16 px-3 py-1.5 text-[14px] font-semibold transition-colors hover:border-inchiostro hover:bg-inchiostro hover:text-carta"
                  >
                    {c.label}
                  </Link>
                ))}
              </div>
            </div>
            <FacetRow
              label={t('common.shade')}
              items={['Chocolate', 'Greenery', 'Ocean', 'Provence', 'Stones']}
              active={shade}
              onPick={(v) => setFilter('shade', v)}
              testid="facet-shade"
            />
          </div>
        </div>
      )}

      <div className="wrap pb-24">
        {products.isPending ? (
          <div
            data-testid="world-grid"
            className="grid grid-cols-2 gap-x-5 gap-y-12 md:grid-cols-3 lg:grid-cols-4"
            aria-busy="true"
          >
            {Array.from({ length: 8 }).map((_, i) => (
              <TileSkeleton key={i} />
            ))}
          </div>
        ) : products.isError ? (
          <p className="prosa">{t('catalog.loadError')}</p>
        ) : items.length === 0 ? (
          <p className="py-16 text-center font-display text-2xl text-inchiostro/50">
            {t('catalog.empty')}
          </p>
        ) : (
          <div
            data-testid="world-grid"
            className="grid grid-cols-2 gap-x-5 gap-y-12 md:grid-cols-3 md:gap-x-7 lg:grid-cols-4"
          >
            {withInspiration(items, INSPIRATION[slug], world?.label || '')}
          </div>
        )}

        {pages > 1 && (
          <div className="mt-16 flex items-center justify-center gap-3">
            <button
              type="button"
              disabled={page <= 1}
              onClick={() => goToPage(page - 1)}
              data-testid="page-prev"
              className="btn btn-line disabled:opacity-30"
            >
              ←
            </button>
            <span className="text-[14px] text-inchiostro/70">
              {page} / {pages}
            </span>
            <button
              type="button"
              disabled={page >= pages}
              onClick={() => goToPage(page + 1)}
              data-testid="page-next"
              className="btn btn-line disabled:opacity-30"
            >
              →
            </button>
          </div>
        )}
      </div>
    </>
  );
}
